// Copyright 2025, University of Maryland, All Rights Reserved

import React, { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import {
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui'

export interface CollapsibleSectionProps {
  /** Label displayed in the toggle button */
  label: string
  /** Initial open state */
  defaultOpen?: boolean
  /** Controlled open state (overrides defaultOpen) */
  open?: boolean
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void
  /** Content to show when expanded */
  children: React.ReactNode
  /** Additional className for the container */
  className?: string
  /** Additional className for the trigger button */
  triggerClassName?: string
}

/**
 * Generic collapsible section with chevron toggle.
 * Uses shadcn Collapsible for accessibility (keyboard nav, ARIA).
 */
export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  label,
  defaultOpen = false,
  open: controlledOpen,
  onOpenChange,
  children,
  className,
  triggerClassName,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? controlledOpen : internalOpen

  const handleOpenChange = (newOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(newOpen)
    }
    onOpenChange?.(newOpen)
  }

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={handleOpenChange}
      className={cn('group', className)}
    >
      <CollapsibleTrigger
        className={cn(
          'flex items-center gap-1.5 text-sm text-muted-foreground',
          'hover:text-foreground transition-colors duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm',
          triggerClassName
        )}
      >
        {isOpen ? (
          <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 transition-transform duration-200" />
        )}
        <span className="font-medium">{label}</span>
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2 data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
        {children}
      </CollapsibleContent>
    </Collapsible>
  )
}
